import { Zap, Ban, Check, Info, PlugZap } from 'lucide-react';
import { Button } from './ui/button';
import { PowerCategoryBadge } from './PowerCategoryBadge';
import { getPowerCategory, getPowerCategoryConfig } from '../../utils/powerCategories';
import { isWindowActiveNow, type TariffScheduleEntry } from '../../utils/tariffSchedule';
import type { Connector } from '../../types';

/**
 * A single connector ("gun") card: status, power/type, tariff schedule and the
 * status-appropriate action.
 *
 * Rendered by both discovery paths — inline in LocationDetailSheet (browsing) and
 * in ChargePointDetailSheet (QR deep link). Keeping one component is the point:
 * the two paths showed the same information and would otherwise drift.
 *
 * Presentational only. Status and tariffs are fetched per charge point by
 * ChargePointConnectors.
 */

/** A connector merged with the live status fields from useChargePointStatus. */
export interface LiveConnector extends Connector {
  rawStatus?: string;
  currentSoc?: number | null;
}

interface ConnectorCardProps {
  connector: LiveConnector;
  /** Time-of-use windows for this EVSE; empty hides the tariff block. */
  schedule: TariffScheduleEntry[];
  /** Owning charge point's connectivity. `false` disables starting a session. */
  isOnline: boolean | undefined;
  onStartCharging?: () => void;
  /** In-flight start request — disable the button so a double tap cannot fire twice. */
  starting?: boolean;
  /**
   * Power and connector type. Suppressed when every gun on the charge point
   * shares them and the charger header has already said so.
   */
  showSpecs?: boolean;
  /**
   * false when the charger never sends OCPP Preparing. Such chargers can be
   * started from `available`; the driver plugs in first and taps Start.
   */
  reportsPreparing?: boolean;
}

/** Format raw OCPP status to a user-friendly label. */
export function formatRawStatus(rawStatus: string): string {
  const normalized = rawStatus.toUpperCase();
  switch (normalized) {
    case 'CHARGING':
      return 'Charging';
    case 'OCCUPIED':
      return 'Occupied';
    case 'FINISHING':
      return 'Finishing';
    case 'SUSPENDED_EV':
      return 'Suspended (EV)';
    case 'SUSPENDED_EVSE':
      return 'Suspended (EVSE)';
    case 'AVAILABLE':
      return 'Available';
    case 'PREPARING':
      return 'Preparing';
    case 'FAULTED':
      return 'Faulted';
    case 'UNAVAILABLE':
      return 'Unavailable';
    case 'RESERVED':
      return 'Reserved';
    default:
      return rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase();
  }
}

/**
 * Colour configuration for a status category.
 * @param status - UI status category (available, occupied, …)
 * @param rawStatus - raw OCPP status, preferred for the label
 */
export function getConnectorStatusConfig(status: string, rawStatus?: string) {
  const label = rawStatus ? formatRawStatus(rawStatus) : status;

  switch (status) {
    case 'available':
      return {
        bg: 'bg-green-50 dark:bg-green-900/20',
        text: 'text-green-700 dark:text-green-400',
        label,
        dotColor: 'bg-green-500',
        borderColor: 'border-green-200 dark:border-green-800',
      };
    case 'preparing':
      return {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        text: 'text-blue-700 dark:text-blue-400',
        label,
        dotColor: 'bg-blue-500',
        borderColor: 'border-blue-200 dark:border-blue-800',
      };
    case 'occupied':
      return {
        bg: 'bg-orange-50 dark:bg-orange-900/20',
        text: 'text-orange-700 dark:text-orange-400',
        label,
        dotColor: 'bg-orange-500',
        borderColor: 'border-orange-200 dark:border-orange-800',
      };
    case 'out_of_order':
      return {
        bg: 'bg-red-50 dark:bg-red-900/20',
        text: 'text-red-700 dark:text-red-400',
        label,
        dotColor: 'bg-red-500',
        borderColor: 'border-red-200 dark:border-red-800',
      };
    case 'maintenance':
      return {
        bg: 'bg-yellow-50 dark:bg-yellow-900/20',
        text: 'text-yellow-700 dark:text-yellow-400',
        label,
        dotColor: 'bg-yellow-500',
        borderColor: 'border-yellow-200 dark:border-yellow-800',
      };
    default:
      return {
        bg: 'bg-gray-50 dark:bg-gray-800',
        text: 'text-gray-700 dark:text-gray-300',
        label,
        dotColor: 'bg-gray-500',
        borderColor: 'border-gray-200 dark:border-gray-700',
      };
  }
}

/** Map connector types to full names. */
export function getFullConnectorType(type: string): string {
  const typeMap: { [key: string]: string } = {
    'Type 2 AC': 'Type 2 (IEC 62196)',
    'CCS DC': 'CCS Combo 2 (IEC 62196)',
    'CHAdeMO': 'CHAdeMO (DC)',
  };
  return typeMap[type] || type;
}

/**
 * Border colour for the expanded panel. The row and the card are separate
 * elements that have to read as one box, so both take their edge from here
 * rather than each choosing its own.
 */
export function getConnectorPanelBorder(
  connector: LiveConnector,
  isOnline: boolean | undefined
): string {
  if (connector.active === false || isOnline === false) {
    return 'border-gray-200 dark:border-gray-700';
  }
  return getConnectorStatusConfig(connector.status, connector.rawStatus).borderColor;
}

/** True when this connector is the one a user would act on next. */
export function isActionableConnector(connector: LiveConnector): boolean {
  if (connector.active === false) return false;
  return connector.status === 'available' || connector.status === 'preparing';
}

export function ConnectorCard({ connector, schedule, isOnline, onStartCharging, showSpecs = true, starting = false, reportsPreparing }: ConnectorCardProps) {
  const statusConfig = getConnectorStatusConfig(connector.status, connector.rawStatus);
  // Every block below this reports what the gun is doing. None of it can be
  // trusted once the charge point is unreachable, so an offline charger gets a
  // single honest statement instead of a stale one.
  const offline = isOnline === false;
  // An operator has taken this gun out of service. Ranked above `offline` because
  // it outlives the outage: "unavailable while offline" would imply it comes back
  // with the charger, and it does not.
  const disabled = connector.active === false;
  const gstRate = schedule.find((e) => e.vat != null)?.vat;
  // Whatever is in force leads; the rest keep their given order behind it.
  const orderedSchedule = [...schedule].sort(
    (a, b) => Number(isWindowActiveNow(b)) - Number(isWindowActiveNow(a))
  );

  const OfflineStartButton = () => (
    <div>
      <Button
        className="w-full h-12 rounded-xl bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-bold cursor-not-allowed"
        disabled
      >
        <Zap className="w-5 h-5 mr-2" />
        Start Charging
      </Button>
      <p className="text-xs text-gray-400 text-center mt-1.5">Unavailable while offline</p>
    </div>
  );

  // An element, not a component: a component defined here is a new type every
  // render, so each status refresh would remount the button and drop its focus.
  const startButton = (
    <Button
      className="w-full h-12 rounded-xl bg-[#8bc43e] hover:bg-[#6fa02e] text-white font-bold shadow-lg disabled:opacity-60"
      disabled={starting}
      onClick={() => {
        if (starting) return;
        onStartCharging?.();
      }}
    >
      <Zap className="w-5 h-5 mr-2" fill="white" />
      {starting ? 'Starting...' : 'Start Charging'}
    </Button>
  );

  return (
    <div
      className={`bg-gray-50 dark:bg-gray-800 rounded-b-xl px-4 pt-3 pb-4 border border-t-0 ${
        disabled || offline ? 'border-gray-200 dark:border-gray-700' : statusConfig.borderColor
      }`}
    >
      {/* Specs, only when the charger header could not speak for every gun */}
      {showSpecs && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Power</p>
            <p className="font-bold text-gray-900 dark:text-white">{connector.power}</p>
            <PowerCategoryBadge
              category={getPowerCategoryConfig(getPowerCategory(parseFloat(connector.power) || 0))}
              size="sm"
            />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Type</p>
            <p className="font-semibold text-sm text-gray-900 dark:text-white">{getFullConnectorType(connector.type)}</p>
          </div>
        </div>
      )}

      {/* Rates. Solar and non-solar arrive as two separate tariffs, so two lines
          is the normal shape rather than the exception — which is why this is a
          plain list and not a bordered schedule table. The rate in force leads,
          because that is the one being looked for. */}
      {schedule.length > 0 && (
        <div className="mb-4 space-y-1">
          {orderedSchedule.map((entry, idx) => {
            const activeNow = isWindowActiveNow(entry);
            const hasWindow = !!(entry.startTime && entry.endTime);
            const when = !hasWindow
              ? ''
              : activeNow
                ? `Now · until ${entry.endTime}`
                : `From ${entry.startTime}`;
            return (
              <div key={idx} className="flex items-baseline gap-2">
                <span
                  className={
                    activeNow
                      ? 'font-bold text-gray-900 dark:text-white'
                      : 'text-sm font-medium text-gray-500 dark:text-gray-400'
                  }
                >
                  ₹{entry.price}/kWh
                </span>
                {when && (
                  <span
                    className={
                      activeNow
                        ? 'text-xs font-semibold text-[#8bc43e]'
                        : 'text-xs text-gray-400 dark:text-gray-500'
                    }
                  >
                    {when}
                  </span>
                )}
                {idx === orderedSchedule.length - 1 && gstRate != null && (
                  <span className="ml-auto text-[11px] text-gray-400 dark:text-gray-500">
                    + {gstRate}% GST
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Status-based Actions — withheld while out of service or offline */}
      {disabled ? (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 flex items-center gap-3">
          <Ban className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
          <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
            This connector has been taken out of service
          </p>
        </div>
      ) : offline ? (
        <OfflineStartButton />
      ) : (
        <>
        {connector.status === 'available' && (
          <div className={`${statusConfig.bg} border ${statusConfig.borderColor} rounded-xl p-4 flex flex-col gap-3`}>
            <div className="flex items-center gap-3.5">
              {/* The plug seats into the EVee Buddy socket — the brand mark drawn
                  inline so it takes currentColor and stays crisp at any size. */}
              <div className="relative flex-none h-[52px] w-[100px]">
                <svg
                  className="plug-socket absolute right-0 top-1/2 h-[34px] w-[34px] text-blue-500 dark:text-blue-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                  <rect x="8.8" y="8.2" width="1.6" height="3.9" rx="0.5" fill="currentColor" />
                  <rect x="13.6" y="8.2" width="1.6" height="3.9" rx="0.5" fill="currentColor" />
                  <rect x="10.35" y="13.3" width="3.3" height="2.2" rx="0.5" fill="currentColor" />
                </svg>
                <div className="plug-seat absolute left-0 top-1/2 flex h-8 w-8 items-center justify-center rounded-[9px] bg-blue-500">
                  <PlugZap className="h-4 w-4 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-blue-900 dark:text-blue-300 text-lg leading-tight">Ready to Charge!</p>
                <p className="text-sm text-blue-700 dark:text-blue-400 mt-0.5">
                  {reportsPreparing === false ? 'Plug in your car, then tap Start Charging' : 'Plug in your car'}
                </p>
              </div>
            </div>

            {/* What happens next. The rail advances on the same curve as the plug,
                so the loop makes the causal claim rather than just decorating.
                Labelled with the button's own words, since that is the control
                the user will actually be looking for. */}
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 dark:text-blue-400">
                <span className="h-[15px] w-[15px] flex-none rounded-full border-2 border-blue-500 bg-blue-500" />
                Plug in
              </span>
              <span className="relative h-0.5 flex-1 overflow-hidden rounded-full bg-gray-300 dark:bg-gray-600">
                <span className="plug-rail-fill absolute inset-y-0 left-0 bg-blue-500" />
              </span>
              <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400">
                <span className="plug-next-bead h-[15px] w-[15px] flex-none rounded-full border-2 border-blue-500" />
                Start Charging
              </span>
            </div>

            {reportsPreparing === false && startButton}
          </div>
        )}

        {connector.status === 'preparing' && (
          <div className="flex flex-col gap-3">
            {/* The same rail the available state loops through, now reporting fact
                instead of intent: the car is plugged in, and the step it has been
                pointing at is the button directly below. Nothing animates here —
                the sequence has arrived, so there is nothing left to promise. */}
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 dark:text-blue-400">
                <span className="flex h-[15px] w-[15px] flex-none items-center justify-center rounded-full border-2 border-blue-500 bg-blue-500">
                  <Check className="h-2.5 w-2.5 text-white" strokeWidth={4} />
                </span>
                Plugged in
              </span>
              <span className="h-0.5 flex-1 rounded-full bg-blue-500" />
              <span className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 dark:text-blue-400">
                <span className="plug-bead-live h-[15px] w-[15px] flex-none rounded-full border-2 border-blue-500 bg-blue-500 text-blue-500" />
                Start Charging
              </span>
            </div>
            {startButton}
          </div>
        )}

        {connector.status === 'occupied' && connector.rawStatus?.toUpperCase() !== 'CHARGING' && (
          <div className={`${statusConfig.bg} border ${statusConfig.borderColor} rounded-xl p-3 flex items-center gap-3`}>
            <Info className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0" />
            <p className="text-sm text-orange-700 dark:text-orange-400 font-medium">Currently in use by another vehicle</p>
          </div>
        )}

        {connector.status === 'out_of_order' && (
          <div className={`${statusConfig.bg} border ${statusConfig.borderColor} rounded-xl p-3 flex items-center gap-3`}>
            <Info className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-400 font-medium">Out of order</p>
          </div>
        )}

        {connector.status === 'maintenance' && (
          <div className={`${statusConfig.bg} border ${statusConfig.borderColor} rounded-xl p-3 flex items-center gap-3`}>
            <Info className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
            <p className="text-sm text-yellow-700 dark:text-yellow-400 font-medium">Under maintenance</p>
          </div>
        )}

        {(connector.status === 'charging' || (connector.status === 'occupied' && connector.rawStatus?.toUpperCase() === 'CHARGING')) && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-3">
            <div className="flex items-center gap-3">
              <Zap className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <div className="flex-1">
                {connector.currentSoc != null ? (
                  <>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                        Battery: {connector.currentSoc}%
                      </p>
                    </div>
                    <div className="w-full h-2 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 dark:bg-blue-400 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(Math.max(connector.currentSoc, 0), 100)}%` }}
                      />
                    </div>
                  </>
                ) : (
                  <p className="text-sm font-medium text-blue-700 dark:text-blue-400">Charging in progress</p>
                )}
              </div>
            </div>
          </div>
        )}
        </>
      )}
    </div>
  );
}
